import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Gravador de nota de voz (microfone do navegador → MediaRecorder). O formato que sai depende do navegador (WebM/Opus no
 * Chrome e no Edge, OGG/Opus no Firefox, MP4 no Safari); o SERVIDOR converte para OGG/Opus mono, que é o que o WhatsApp
 * mostra como mensagem de voz (docs/attachments.md).
 */

export interface RecordedVoice {
  blob: Blob;
  mimeType: string;
  seconds: number;
}

/** O tempo máximo de uma gravação (o servidor aceita 20 min; 10 já é uma nota de voz bem longa). */
export const MAX_RECORD_SECONDS = 10 * 60;

const CANDIDATES = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

/** O navegador sabe gravar áudio? */
export function canRecordVoice(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function'
  );
}

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder.isTypeSupported !== 'function') return undefined;
  return CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type));
}

/** O motivo, em português, de o microfone não ter ligado. */
export function microphoneError(error: unknown): string {
  const name = (error as { name?: string } | null)?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'O navegador não deixou usar o microfone. Permita o microfone para este endereço e tente de novo.';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return 'Nenhum microfone foi encontrado neste computador.';
  }
  if (name === 'NotReadableError') {
    return 'O microfone está em uso por outro programa. Feche-o e tente de novo.';
  }
  return 'Não foi possível ligar o microfone. Tente de novo.';
}

export type RecorderStatus = 'idle' | 'asking' | 'recording';

export interface VoiceRecorder {
  status: RecorderStatus;
  /** Segundos gravados (atualiza a cada segundo). */
  seconds: number;
  error: string | null;
  start: () => Promise<void>;
  /** Encerra e entrega a gravação (`null` se não havia nada gravado). */
  stop: () => Promise<RecordedVoice | null>;
  cancel: () => void;
  dismissError: () => void;
}

export function useVoiceRecorder(): VoiceRecorder {
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const chunks = useRef<Blob[]>([]);
  const startedAt = useRef(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  // Cancelar enquanto o navegador ainda pergunta pela permissão: a resposta que chegar depois é ignorada.
  const attempt = useRef(0);

  const release = useCallback((): void => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
    recorder.current = null;
    chunks.current = [];
  }, []);

  const cancel = useCallback((): void => {
    attempt.current += 1;
    const current = recorder.current;
    if (current && current.state !== 'inactive') {
      current.ondataavailable = null;
      current.onstop = null;
      current.stop();
    }
    release();
    setSeconds(0);
    setStatus('idle');
  }, [release]);

  // Sair da conversa no meio da gravação desliga o microfone.
  useEffect(() => () => release(), [release]);

  const start = useCallback(async (): Promise<void> => {
    if (status !== 'idle') return;
    setError(null);
    if (!canRecordVoice()) {
      setError('Este navegador não consegue gravar áudio.');
      return;
    }
    const mine = ++attempt.current;
    setStatus('asking');
    let media: MediaStream;
    try {
      media = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
      });
    } catch (err) {
      if (mine === attempt.current) {
        setError(microphoneError(err));
        setStatus('idle');
      }
      return;
    }
    if (mine !== attempt.current) {
      media.getTracks().forEach((track) => track.stop());
      return;
    }
    const mimeType = pickMimeType();
    try {
      const created = new MediaRecorder(media, {
        ...(mimeType ? { mimeType } : {}),
        audioBitsPerSecond: 32_000,
      });
      created.ondataavailable = (event: BlobEvent): void => {
        if (event.data.size > 0) chunks.current.push(event.data);
      };
      stream.current = media;
      recorder.current = created;
      chunks.current = [];
      startedAt.current = Date.now();
      created.start(1000);
      setSeconds(0);
      setStatus('recording');
      timer.current = setInterval(() => {
        setSeconds(
          Math.min(MAX_RECORD_SECONDS, Math.floor((Date.now() - startedAt.current) / 1000)),
        );
      }, 250);
    } catch {
      media.getTracks().forEach((track) => track.stop());
      setError('Este navegador não consegue gravar áudio.');
      setStatus('idle');
    }
  }, [status]);

  const stop = useCallback((): Promise<RecordedVoice | null> => {
    const current = recorder.current;
    if (!current || current.state === 'inactive') {
      cancel();
      return Promise.resolve(null);
    }
    const started = startedAt.current;
    return new Promise((resolve) => {
      current.onstop = (): void => {
        const mimeType = current.mimeType || chunks.current[0]?.type || 'audio/webm';
        const blob = new Blob(chunks.current, { type: mimeType });
        const elapsed = Math.max(1, Math.round((Date.now() - started) / 1000));
        release();
        setSeconds(0);
        setStatus('idle');
        resolve(blob.size > 0 ? { blob, mimeType, seconds: elapsed } : null);
      };
      current.stop();
    });
  }, [cancel, release]);

  // Passou do máximo: a gravação termina sozinha (o ChatPane a envia ao ver `seconds` no teto).
  return {
    status,
    seconds,
    error,
    start,
    stop,
    cancel,
    dismissError: useCallback(() => setError(null), []),
  };
}
